import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../../../services/api-base';
import type {
  AccountOverview,
  AccountViewChildrenQuery,
  AccountViewPage,
  AccountViewQuery,
} from '../types/account-view.types';

/**
 * Accounts-console API (DV-2958) — the server-side replacement for fetching
 * every account and filtering, sorting and paginating in the browser.
 *
 * The console previously loaded all ~1,700 accounts with all 30 fields, which is
 * what made its endpoint pay ~35 s of cross-service aggregation. It cannot be
 * fixed by fetching counts for only the visible rows, because the table sorts on
 * counts and rolls child counts into parents — you cannot sort or roll up on a
 * field you never fetched. So the whole operation moves to the database, and this
 * slice is the wire for it.
 *
 * **The paths must not contain the substring "admin".** They were originally
 * `/entities/admin-view`, and AWS WAF's managed admin-protection rule 403s any
 * URI containing `admin` at the load balancer — before APISIX or the service.
 * It cannot be reproduced locally: a local UI points straight at the service and
 * never crosses the ALB, which is how this reached QA rendering an empty console
 * with no error. entity-service has a test pinning the server side.
 */

/**
 * Maps the console's filter state onto query parameters.
 *
 * Two things are deliberate. `'All'` and empty arrays are omitted rather than
 * sent, so the backend's "not set" branch and the UI's "no filter" state cannot
 * drift apart. And the viewer's UTC offset travels with any date filter: the
 * console filters on the date *as the viewer sees it*, so a UTC-anchored range
 * would put a late-evening account on the wrong day for anyone east or west of
 * UTC.
 */
const toParams = ({ filters: f, sortKey, sortDir, page, size }: AccountViewQuery) => {
  const params: Record<string, string | number | string[]> = {
    sort: sortKey,
    dir: sortDir,
    page,
    size,
  };

  const q = f.q?.trim();
  if (q) params.q = q;
  if (f.status && f.status !== 'All') params.status = f.status;
  if (f.billing && f.billing !== 'All') params.billing = f.billing;
  if (f.category?.length) params.category = f.category;
  if (f.pms?.length) params.pms = f.pms;
  if (f.origins?.length) params.origin = f.origins;

  const createdByText = f.createdByText?.trim();
  if (createdByText) {
    params.createdByText = createdByText;
    params.createdByMode = f.createdByMode ?? 'contains';
  }

  if (f.dateMode && f.dateMode !== 'All') {
    params.dateMode = f.dateMode;
    if (f.dateMode === 'on' && f.dateOn) params.dateOn = f.dateOn;
    if (f.dateMode === 'between') {
      if (f.dateFrom) params.dateFrom = f.dateFrom;
      if (f.dateTo) params.dateTo = f.dateTo;
    }
    // getTimezoneOffset() is minutes to ADD to local to reach UTC, i.e. the
    // negation of the offset. Negate it back so the server receives the
    // conventional sign (UTC-5 → -300).
    params.tzOffsetMinutes = -new Date().getTimezoneOffset();
  }

  return params;
};

export const accountViewApi = createApi({
  reducerPath: 'accountViewApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /**
     * GET /entity-service/api/v1/entities/account-view — one page of accounts,
     * already filtered and sorted. INTERNAL_ADMIN only; the endpoint enforces
     * that server-side rather than trusting the route guard.
     */
    getAccountView: builder.query<AccountViewPage, AccountViewQuery>({
      query: (args) => ({
        url: '/entity-service/api/v1/entities/account-view',
        params: toParams(args),
      }),
    }),

    /**
     * GET .../account-view/{id}/children — fetched on demand rather than embedded
     * in the parent row: the table shows at most ten inline before deferring to
     * the drawer, and the drawer needs its own search and paging anyway.
     */
    getAccountViewChildren: builder.query<AccountViewPage, AccountViewChildrenQuery>({
      query: ({ parentEntityId, q, page, size }) => ({
        url: `/entity-service/api/v1/entities/account-view/${encodeURIComponent(parentEntityId)}/children`,
        params: q?.trim() ? { q: q.trim(), page, size } : { page, size },
      }),
    }),

    /**
     * GET .../account-view/{entityId}/overview (DV-3363). One account's Overview,
     * its stored fields read live from `entities`. INTERNAL_ADMIN only, enforced
     * by the service. The account is named in the path, never by the session's
     * account or an `X-Entity-UUID` (D-06). Callers pass
     * `refetchOnMountOrArgChange: true`: a cached copy would hide a Legacy edit.
     */
    getAccountOverview: builder.query<AccountOverview, string>({
      query: (entityId) => ({
        url: `/entity-service/api/v1/entities/account-view/${encodeURIComponent(entityId)}/overview`,
      }),
    }),
  }),
});

export const {
  useGetAccountViewQuery,
  useLazyGetAccountViewQuery,
  useGetAccountViewChildrenQuery,
  useLazyGetAccountViewChildrenQuery,
  useGetAccountOverviewQuery,
} = accountViewApi;
